import { z } from 'zod';

const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

/* Monthly Merch KPI cards (8170–8172). All three share the
   (month, brand_name, brand_manager, merch_portfolio) grain, months relative
   to today (last 9 months; size availability carries 6 month-end snapshots +
   live). Brand grain (added 2026-06-11) powers the per-BM brand breakdown. */

export const MonthlyKpiRevenueSchema = z.array(z.object({
  month: z.string(),
  brand_name: z.string(),
  brand_manager: z.string(),
  merch_portfolio: z.string(),
  net_gmv: num(),
  online_net_gmv: num(),
}));
export type MonthlyKpiRevenue = z.infer<typeof MonthlyKpiRevenueSchema>;

export const MonthlyKpiSizeAvailabilitySchema = z.array(z.object({
  month: z.string(),
  brand_name: z.string(),
  brand_manager: z.string(),
  merch_portfolio: z.string(),
  /* DAY-SUMS over the month (2026-06-11): numerator/denominator of the
     daily-averaged ratios. avg = sizes/styles; 3plus % = 3plus/styles. */
  in_stock_styles: num(),
  total_unique_sizes: num(),
  avg_size_availability: num(),
  styles_3plus_sizes: num().optional().default(0),
  days_counted: num().optional(),
}));
export type MonthlyKpiSizeAvailability = z.infer<typeof MonthlyKpiSizeAvailabilitySchema>;

/* Brand Health Score components — same seven-drag formula as the
   MTO / MTC / JIT-B2B Health report (card 7763 / Dashboard 195). */
export const MonthlyKpiCxSchema = z.array(z.object({
  month: z.string(),
  brand_name: z.string(),
  brand_manager: z.string(),
  merch_portfolio: z.string(),
  shared_lines: num(),
  total_customers: num(),
  declined_lines: num(),
  wh_delay_1d_scope_lines: num(),
  wh_delay_1d_lines: num(),
  wh_delay_7d_scope_lines: num(),
  wh_delay_7d_lines: num(),
  delay_cancelled_lines: num(),
  wh_delayed_without_timely_revision_lines: num(),
  decline_days_sum: numNull(),
  decline_days_cnt: num(),
  impacted_customers: num(),
  /* Customer-distinct counts deduped across the BM's whole book — exact for
     BM rollups (brand-grain distincts double-count cross-brand customers). */
  bm_total_customers: numNull().optional(),
  bm_impacted_customers: numNull().optional(),
  /* Customer-distinct counts deduped across the portfolio's whole book —
     exact for portfolio rollups when the BM filter is not narrowing scope. */
  portfolio_total_customers: numNull().optional(),
  portfolio_impacted_customers: numNull().optional(),
}));
export type MonthlyKpiCx = z.infer<typeof MonthlyKpiCxSchema>;
